/* eslint-disable max-len */
import _findKey from 'lodash/findKey';
import _find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import { findTagIndex } from '../tags';
import { calculateChoiceValuesPostFormLaminate, calculateChoiceValuesCustomLaminate } from './calculations';
import { customSplashesTag, postformEndSplashesTag, LAMINATE_PRODUCT_ID } from '../../common/constants';
import loadConfiguratorOptionsBySku from '../../actions/configurator/loadConfiguratorOptionsBySku';

export const choiceCustomOrPostform = (choice, allSurfaceColors, designClassification) => {
  const matchingColors = allSurfaceColors.filter(
    (color) => color.optionId === choice.externalId
  );

  const postFormColors = matchingColors.filter((color) => color.postFormSurfaceCode === true);
  const customColors = matchingColors.filter((color) => color.postFormSurfaceCode === false);

  const updatedSurfaceCode =
    designClassification === 'postform'
      ? postFormColors[0]?.surfaceCode || null
      : customColors[0]?.surfaceCode || null;

  const updatedBrand =
    designClassification === 'postform'
      ? postFormColors[0]?.brand || null
      : customColors[0]?.brand || null;

  const postFormSku = postFormColors.map((color) => color.sku);
  const customSku = customColors.map((color) => color.sku);

  return {
    ...choice,
    surfaceCode: updatedSurfaceCode,
    postFormSku,
    customSku,
    brand: updatedBrand,
    postFormSurfaceCode: designClassification === 'postform'
  };
};

export const customOrPostformSkus = (allSurfaceColors, designClassification) => {
  const customColors = allSurfaceColors.filter((color) => color.postFormSurfaceCode === false);
  const postFormColors = allSurfaceColors.filter((color) => color.postFormSurfaceCode === true);

  const allSkus = [...new Set(allSurfaceColors.map((color) => color.sku))].join(',');
  const customSkus = [...new Set(customColors.map((color) => color.sku))].join(',');
  const postFormSkus = [...new Set(postFormColors.map((color) => color.sku))].join(',');

  let availableSkusString = allSkus;
  if (designClassification !== 'none') {
    availableSkusString = designClassification === 'custom' ?
      customSkus : postFormSkus;
  }

  return availableSkusString;
};

export const customOrPostformSurfaceCodes = (allSurfaceColors, designClassification) => {
  const customColors = allSurfaceColors.filter((color) => color.postFormSurfaceCode === false);
  const postFormColors = allSurfaceColors.filter((color) => color.postFormSurfaceCode === true);

  const allSurfaceCodes = [...new Set(allSurfaceColors.map((color) => color.surfaceCode))].join(',');
  const customSurfaceCodes = [...new Set(customColors.map((color) => color.surfaceCode))].join(',');
  const postFormSurfaceCodes = [...new Set(postFormColors.map((color) => color.surfaceCode))].join(',');

  let availableSurfaceCodes = allSurfaceCodes;
  if (designClassification !== 'none') {
    availableSurfaceCodes = designClassification === 'custom' ?
      customSurfaceCodes : postFormSurfaceCodes;
  }

  return availableSurfaceCodes;
};

export const findIdByTag = (col, tag) => +_findKey(col, (xx) => xx.tags && findTagIndex(xx.tags, tag) > -1);

const getLaminateColorGroupChoice = (optionId, selectedColor, choices) => {
  const { ChoiceId, id } = selectedColor || {};
  const cid = ChoiceId || id;

  const colorChoice = _find(choices, (xx) => xx.id === cid);
  const { parentOptionId } = colorChoice || {};
  const colorGroupChoice = _find(choices, (xx) => xx.optionId === optionId
    && xx.tags && findTagIndex(xx.tags, `colorparentextid:${parentOptionId}`) > -1);

  return colorGroupChoice || null;
};

const selectLaminateColorGroupChoice = (optionId, laminateColorGroupChoice, value, onChoiceSelected, laminatePostFormFeatureGate) => {
  if (laminateColorGroupChoice && (!laminateColorGroupChoice.isSelected || laminateColorGroupChoice.value !== value)) {
    const { id } = laminateColorGroupChoice;

    if (laminatePostFormFeatureGate) {
      // eslint-disable-next-line no-console
      console.log('selectLaminateColorGroupChoice', optionId, id, value); // Remove after testing
    }
    onChoiceSelected(optionId, id, value);
  }
};

const unselectLaminateColorGroupChoice = (optionId, choices, onChoiceSelected, laminatePostFormFeatureGate) => {
  const choice = _find(choices, (xx) => xx.optionId === optionId && xx.isSelected);
  if (choice) {
    const { id, value } = choice;
    if (laminatePostFormFeatureGate) {
      // eslint-disable-next-line no-console
      console.log('unselectLaminateColorGroupChoice', optionId, id, value); // Remove after testing
    }
    onChoiceSelected(optionId, id, value);
  }
};

export const handleLaminateColorGroup = ({ designClassification, shapes, options, selectedColor, choices, onChoiceSelected, laminatePostFormFeatureGate, productId }) => {
  const customSplashesOptionId = findIdByTag(options, customSplashesTag);
  const postformEndSplashesOptionId = findIdByTag(options, postformEndSplashesTag);
  const isLaminate = Number(productId) === LAMINATE_PRODUCT_ID;

  if ((!designClassification || designClassification === 'none') && isLaminate) {
    // Unselect any selected choices for customSplashesOptionId and postformEndSplashesOptionId
    [customSplashesOptionId, postformEndSplashesOptionId].forEach((optionId) => {
      Object.values(choices).forEach((choice) => {
        if (choice.optionId === optionId && choice.isSelected) {
          unselectLaminateColorGroupChoice(optionId, choices, onChoiceSelected, laminatePostFormFeatureGate);
        }
      });
    });
    return;
  }

  if (designClassification === 'postform') {
    const result = calculateChoiceValuesPostFormLaminate(shapes);
    const { totalEndSplashes } = result;

    const laminateColorGroupChoice = getLaminateColorGroupChoice(postformEndSplashesOptionId, selectedColor, choices);
    selectLaminateColorGroupChoice(postformEndSplashesOptionId, laminateColorGroupChoice, totalEndSplashes, onChoiceSelected, laminatePostFormFeatureGate);

    unselectLaminateColorGroupChoice(customSplashesOptionId, choices, onChoiceSelected, laminatePostFormFeatureGate);
  } else {
    const result = calculateChoiceValuesCustomLaminate(shapes);
    const { backsplashArea } = result;

    const laminateColorGroupChoice = getLaminateColorGroupChoice(customSplashesOptionId, selectedColor, choices);
    selectLaminateColorGroupChoice(customSplashesOptionId, laminateColorGroupChoice, backsplashArea, onChoiceSelected, laminatePostFormFeatureGate);

    unselectLaminateColorGroupChoice(postformEndSplashesOptionId, choices, onChoiceSelected, laminatePostFormFeatureGate);
  }
};

export const getLaminateColorChoices = (designClassification) => (dispatch, getState) => {

  const { surfaces, allSurfaceColors } = getState().countertops;

  const updatedSkus = customOrPostformSkus(allSurfaceColors, designClassification);
  const updatedSurfaceCodes = customOrPostformSurfaceCodes(allSurfaceColors, designClassification);

  const currentSku = updatedSkus.split(',')[0];
  const firstSurfaceCode = Number(updatedSurfaceCodes.split(',')[0]);

  const updatedCurrentSurface = surfaces.find((surface) => surface?.surfaceCode === firstSurfaceCode);

  dispatch({
    type: 'SET_SURFACE',
    availableSkus: updatedSkus,
    currentSurfaceCodes: updatedSurfaceCodes,
    surface: updatedCurrentSurface
  });
  dispatch({
    type: 'SET_CURRENT_SKU',
    sku: updatedCurrentSurface?.skus[0]?.sku,
    height: updatedCurrentSurface?.skus[0]?.height
  });

  dispatch(loadConfiguratorOptionsBySku(currentSku));

};

export const validateSelectedColor = ({ options, choices, onChoiceSelected, selectedColor, setSelectedColor }) => {
  const laminateColorOption = Object.values(options).find(
    (obj) => obj?.internalName === 'color_countertops_laminate'
  );

  Object.values(choices).forEach((choice) => {
    const isSameOption = choice?.optionId === laminateColorOption?.id;
    const isSelected = choice?.isSelected === true;

    if (isSameOption && isSelected) {
      const existsInLaminateChoices = laminateColorOption?.choices.includes(choice?.id);

      if (isEmpty(selectedColor) && existsInLaminateChoices) {
        setSelectedColor(choice);

      } else if (!existsInLaminateChoices) {

        onChoiceSelected(laminateColorOption?.id, choice?.id, 0);
      }
    }
  });
};
